import { FC, useState } from 'react';
import { useUploadSession } from '../../../services/upload/UploadProvider';
import { OneDriveUploadSession } from '../../../services/upload/OneDriveUploadProvider';
import { DocumentIcon } from '../../../components/oneDrive/DocumentIcon';
import { UploadStatus } from '../../../services/upload/utils';
import { FiX, FiUpload, FiCheck, FiAlertTriangle, FiAlertOctagon, FiArrowRight, FiFolder } from 'react-icons/fi';
import { RetryModal } from './RetryModal';
import { IconType } from 'react-icons';
import { Loader } from '../../../components/loading/Loader';
import { useNavigate } from 'react-router-dom';
import { merge } from '../../../utils/utility';

const ItemButton: FC<{ text: string; Icon: IconType; onClick: () => void }> = ({ text, Icon, onClick }) => {
  return (
    <button
      type="button"
      className="w-full bg-primary-std p-2 border-t flex items-center justify-center gap-2"
      onClick={onClick}
    >
      <Icon className="h-5 w-5" />
      <span>{text}</span>
    </button>
  );
};

const CancelButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  return <ItemButton text="Cancel" Icon={FiX} onClick={onClick} />;
};

const RetryButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  return <ItemButton text="Retry" Icon={FiUpload} onClick={onClick} />;
};

const OkayButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  return <ItemButton text="OK" Icon={FiCheck} onClick={onClick} />;
};

const DocumentsButton: FC = () => {
  const navigate = useNavigate();

  const onClick = () => {
    navigate('/documents/myDocuments');
  };

  return <ItemButton text="View in My Documents" Icon={FiFolder} onClick={onClick} />;
};

interface ItemConfig {
  canCancel?: boolean;
  canRetry?: boolean;
  canHide?: boolean;
  canGoToDocuments?: boolean;
  showWarning?: boolean;
  showError?: boolean;
  showLoader?: boolean;
  message?: string;
}

const ItemContent: FC<{ id: string }> = ({ id }: { id: string }) => {
  const [session, hide, cancel, retry] = useUploadSession<OneDriveUploadSession>(id);
  const conf: ItemConfig = {};

  switch (session.status) {
    case UploadStatus.Complete:
      conf.message = 'Upload completed successfully!';
      conf.canGoToDocuments = true;
      conf.canHide = true;
      break;
    case UploadStatus.Cancelled:
      conf.message = 'Upload cancelled.';
      conf.canHide = true;
      break;
    case UploadStatus.Failed:
      conf.message = 'Upload failed.';
      conf.canRetry = true;
      conf.canCancel = true;
      conf.showError = true;
      break;
    case UploadStatus.Interrupted:
      conf.message = 'Upload interrupted.';
      conf.canRetry = true;
      conf.canCancel = true;
      conf.showWarning = true;
      break;
    case UploadStatus.Uploading:
      conf.message = 'Upload in progress.';
      conf.canCancel = true;
      conf.showLoader = true;
      break;
    case UploadStatus.Waiting:
      conf.message = 'Waiting for upload to begin.';
      conf.canCancel = true;
      break;
  }

  const [retryModalOpen, setRetryModalOpen] = useState<boolean>(false);

  return (
    <div>
      {conf.showWarning && <FiAlertTriangle className="w-16 h-16 mx-auto mt-3" />}
      {conf.showError && <FiAlertOctagon className="w-16 h-16 mx-auto mt-3 text-secondary-800" />}
      {conf.showLoader && <Loader className="mt-3 w-8 h-8 mx-auto" />}
      <p className="text-center p-3">{conf.message}</p>
      {conf.canRetry && <RetryButton onClick={() => setRetryModalOpen(true)} />}
      {conf.canCancel && <CancelButton onClick={cancel}></CancelButton>}
      {conf.canGoToDocuments && <DocumentsButton />}
      {conf.canHide && <OkayButton onClick={hide} />}
      <RetryModal
        open={retryModalOpen}
        description="Select the file again, and the upload will be resumed or restarted."
        onSubmit={async (files: FileList) => {
          if (files.length === 0) {
            throw Error('No file provided for retry upload');
          }
          if (files.length > 1) {
            throw Error('Only a single file must be provided');
          }
          await retry(files.item(0) as File);
        }}
        onClose={() => setRetryModalOpen(false)}
      />
    </div>
  );
};

export const Item: FC<{ id: string }> = ({ id }: { id: string }) => {
  const [session, hide] = useUploadSession<OneDriveUploadSession>(id);

  let statusString = session.status as string;
  if (session.status === UploadStatus.Uploading) {
    const uploaded = session.progress?.at(-1)?.range?.end ?? 0;
    const total = session.size ?? 0;
    const percentage = (uploaded / total) * 100;
    statusString += ` - ${percentage.toFixed()}%`;
  }

  const hideable = session.status === UploadStatus.Complete || session.status === UploadStatus.Cancelled;

  const error = session.status === UploadStatus.Failed;

  return (
    <div className="border rounded-md bg-primary-std overflow-hidden">
      <div className={merge('py-2 px-3 flex items-center gap-2', error ? 'bg-secondary-800' : 'bg-primary-800')}>
        <DocumentIcon fileName={session.name} />
        <div className="w-full">
          <div className="flex justify-between">
            <p className="font-semibold text-white">{session.name}</p>
            {hideable && (
              <button className="text-white" onClick={hide}>
                <FiX className="h-6 w-6" />
              </button>
            )}
          </div>
          <p className="text-gray-300">{statusString}</p>
        </div>
      </div>
      <ItemContent id={id} />
    </div>
  );
};
